/**
 * Order Detail Page
 *
 * Display full order information with status timeline, actions, and provider contact.
 * Uses DDD-aligned application service layer for authorization and data transformation.
 */

import { useMemo, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useWidgetAuth } from '../../contexts/WidgetAuthContext';
import { WidgetApiClient } from '../../services/widget/WidgetApiClient';
import { WidgetOrderService } from '../../services/widget/WidgetOrderService';
import { WidgetOrderApplicationService, useOrder, useRequestPickup, useCancelOrder } from '../../application/widget';
import { OrderStatusBadge } from '../../components/widget/OrderStatusBadge';
import { OrderTimeline } from '../../components/widget/OrderTimeline';
import { RequestPickupModal } from '../../components/widget/RequestPickupModal';
import { CancelOrderModal } from '../../components/widget/CancelOrderModal';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';

export function OrderDetail() {
  const { providerSlug, orderId } = useParams<{
    providerSlug: string;
    orderId: string;
  }>();
  const { token, isAuthenticated, user } = useWidgetAuth();
  const navigate = useNavigate();

  const [isPickupModalOpen, setIsPickupModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  // Initialize application service with memoization
  const orderApplicationService = useMemo(() => {
    if (!isAuthenticated || !user?.customerId) return null;
    const widgetOrderService = new WidgetOrderService(
      providerSlug!,
      new WidgetApiClient(() => token)
    );
    return new WidgetOrderApplicationService(widgetOrderService, user.customerId);
  }, [providerSlug, token, isAuthenticated, user]);

  // Use React Query hook for data fetching
  const {
    data: order,
    isLoading,
    error,
  } = useOrder(orderApplicationService!, orderId!, {
    enabled: !!orderApplicationService && !!orderId,
  });

  // Mutations for actions
  const requestPickupMutation = useRequestPickup(orderApplicationService!);
  const cancelOrderMutation = useCancelOrder(orderApplicationService!);

  // Handle pickup request
  const handleRequestPickup = async (data: {
    requestedPickupDate?: string;
    notes?: string;
  }) => {
    try {
      await requestPickupMutation.mutateAsync({
        orderId: orderId!,
        data,
      });
      toast.success('Pickup requested successfully!');
      setIsPickupModalOpen(false);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to request pickup');
    }
  };

  // Handle cancel order
  const handleCancelOrder = async (data: { reason: string; notes?: string }) => {
    try {
      await cancelOrderMutation.mutateAsync({
        orderId: orderId!,
        data,
      });
      toast.success('Order cancelled successfully');
      navigate(`/widget/${providerSlug}/orders`);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to cancel order');
    }
  };

  // Redirect if not authenticated
  if (!isAuthenticated) {
    navigate(`/widget/${providerSlug}/login`);
    return null;
  }

  // Loading skeleton
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="mb-6">
            <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
          </div>
          <div className="space-y-6">
            <Card className="animate-pulse">
              <CardHeader>
                <div className="h-6 w-32 bg-gray-200 rounded" />
              </CardHeader>
              <CardContent>
                <div className="h-32 bg-gray-200 rounded" />
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (error || (!isLoading && !order)) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
            <svg
              className="w-12 h-12 text-red-400 mx-auto mb-4"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <h3 className="text-lg font-semibold text-red-900 mb-2">
              Order Not Found
            </h3>
            <p className="text-red-700 mb-4">
              {error instanceof Error ? error.message : 'This order does not exist'}
            </p>
            <Link to={`/widget/${providerSlug}/orders`}>
              <Button>Back to Orders</Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!order) return null;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Link
            to={`/widget/${providerSlug}/orders`}
            className="text-sm text-blue-600 hover:text-blue-800 mb-2 inline-block"
          >
            ← Back to Orders
          </Link>
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">
                Order #{order.orderNumber}
              </h1>
              <p className="text-gray-600 mt-1">Placed on {order.dates.placedOn}</p>
            </div>
            <OrderStatusBadge status={order.status} />
          </div>
        </div>

        {/* Status Timeline */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Order Status</CardTitle>
          </CardHeader>
          <CardContent>
            <OrderTimeline order={order.rawData} />
          </CardContent>
        </Card>

        {/* Order Information */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Order Information</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">
                  Order Number
                </dt>
                <dd className="text-sm text-gray-900">{order.orderNumber}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">
                  Container Size
                </dt>
                <dd className="text-sm text-gray-900">{order.containerSize}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Material Type</dt>
                <dd className="text-sm text-gray-900">{order.materialType}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Order Type</dt>
                <dd className="text-sm text-gray-900">{order.orderType}</dd>
              </div>
              <div className="md:col-span-2">
                <dt className="text-sm font-medium text-gray-500 mb-1">
                  Service Address
                </dt>
                <dd className="text-sm text-gray-900">
                  {order.serviceAddress.street}
                  <br />
                  {order.serviceAddress.cityStateZip}
                </dd>
              </div>
              {order.placementInstructions && (
                <div className="md:col-span-2">
                  <dt className="text-sm font-medium text-gray-500 mb-1">
                    Placement Instructions
                  </dt>
                  <dd className="text-sm text-gray-900">
                    {order.placementInstructions}
                  </dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>

        {/* Contact Information */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Contact Information</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Name</dt>
                <dd className="text-sm text-gray-900">{order.contactName}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Email</dt>
                <dd className="text-sm text-gray-900">{order.contactEmail}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">Phone</dt>
                <dd className="text-sm text-gray-900">{order.contactPhone}</dd>
              </div>
            </dl>
          </CardContent>
        </Card>

        {/* Important Dates */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Important Dates</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <dt className="text-sm font-medium text-gray-500 mb-1">
                  Requested Delivery
                </dt>
                <dd className="text-sm text-gray-900">{order.dates.requestedDelivery}</dd>
              </div>
              {order.dates.scheduledDelivery && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">
                    Scheduled Delivery
                  </dt>
                  <dd className="text-sm text-gray-900">
                    {order.dates.scheduledDelivery}
                  </dd>
                </div>
              )}
              {order.dates.actualDelivery && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">
                    Actual Delivery
                  </dt>
                  <dd className="text-sm text-gray-900">{order.dates.actualDelivery}</dd>
                </div>
              )}
              {order.dates.actualPickup && (
                <div>
                  <dt className="text-sm font-medium text-gray-500 mb-1">
                    Pickup Date
                  </dt>
                  <dd className="text-sm text-gray-900">{order.dates.actualPickup}</dd>
                </div>
              )}
            </dl>
          </CardContent>
        </Card>

        {/* Pricing (if completed) */}
        {order.pricing && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Pricing Summary</CardTitle>
            </CardHeader>
            <CardContent>
              <dl className="space-y-3">
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Rental Fee</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {order.pricing.rental}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Delivery Fee</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {order.pricing.delivery}
                  </dd>
                </div>
                <div className="flex justify-between">
                  <dt className="text-sm text-gray-600">Disposal Fee</dt>
                  <dd className="text-sm font-medium text-gray-900">
                    {order.pricing.disposal}
                  </dd>
                </div>
                <div className="flex justify-between pt-3 border-t border-gray-200">
                  <dt className="text-base font-semibold text-gray-900">Total</dt>
                  <dd className="text-base font-bold text-gray-900">
                    {order.pricing.total}
                  </dd>
                </div>
              </dl>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        {(order.actions.canRequestPickup || order.actions.canCancel) && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Actions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-3">
                {order.actions.canRequestPickup && (
                  <Button onClick={() => setIsPickupModalOpen(true)}>
                    Request Pickup
                  </Button>
                )}
                {order.actions.canCancel && (
                  <Button
                    variant="destructive"
                    onClick={() => setIsCancelModalOpen(true)}
                  >
                    Cancel Order
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Contact Provider */}
        <Card>
          <CardHeader>
            <CardTitle>Contact Provider</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-gray-600 mb-4">
              Have questions about your order? Get in touch with {order.providerName}.
            </p>
            <div className="flex flex-wrap gap-3">
              {order.providerPhone && (
                <a href={`tel:${order.providerPhone}`}>
                  <Button variant="outline">Call {order.providerPhone}</Button>
                </a>
              )}
              {order.providerEmail && (
                <a href={`mailto:${order.providerEmail}`}>
                  <Button variant="outline">Email {order.providerEmail}</Button>
                </a>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Modals */}
      <RequestPickupModal
        isOpen={isPickupModalOpen}
        onClose={() => setIsPickupModalOpen(false)}
        onConfirm={handleRequestPickup}
        orderNumber={order.orderNumber}
      />

      <CancelOrderModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirm={handleCancelOrder}
        orderNumber={order.orderNumber}
      />
    </div>
  );
}
